"use client";

import { useEffect, useState } from "react";
import SignUpButton from "@/components/SignUpButton";

export default function NewHeroSection() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-orange-50/50 via-white to-white dark:from-slate-900 dark:via-slate-950 dark:to-slate-950">
      {/* Decorative orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-orange-200/30 dark:bg-orange-600/10 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -left-20 w-60 h-60 bg-blue-200/20 dark:bg-blue-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-40 h-40 bg-orange-100/40 dark:bg-orange-600/5 rounded-full blur-2xl" />
      </div>

      <div className="relative container mx-auto max-w-7xl px-6 pt-16 pb-8 lg:pt-24 lg:pb-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left: Text content */}
          <div className={`transition-all duration-700 ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}>
            {/* Meet Sabine pill */}
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-100 to-amber-100 dark:from-orange-500/10 dark:to-amber-500/10 border border-orange-200 dark:border-orange-500/20 text-orange-600 dark:text-orange-300 text-sm font-medium mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
              </span>
              Meet Sabine
            </span>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 dark:text-white leading-[1.1]">
              Paste a LinkedIn URL.{" "}
              <span className="text-orange-600 dark:text-orange-400">
                Get what&apos;s not on LinkedIn, and your next step.
              </span>
            </h1>

            <p className="mt-6 text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-lg">
              Prep5 is AI sales call preparation that researches the person and their company beyond the profile, then tells you the one thing to do next: the opener, the question to ask, the follow-up. About a minute. Straight to your inbox if you want it. Powered by Sabine, your AI sales analyst.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <SignUpButton
                className="px-7 py-3.5 text-base font-semibold rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300"
                text="Try Prep5 Free"
              />
            </div>

            {/* Trust indicators */}
            <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-slate-500 dark:text-slate-400">
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                5 free prospects, unlimited briefs
              </div>
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                No credit card
              </div>
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Setup in seconds
              </div>
            </div>
          </div>

          {/* Right: Sabine illustration with floating UI cards */}
          <div className={`relative flex justify-center lg:justify-end transition-all duration-1000 ease-out delay-200 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}>
            {/* Sabine video */}
            <video
              src="/images/sabine/sabine-video-laptop-1x1.mp4"
              autoPlay
              loop
              muted
              playsInline
              aria-label="Sabine, the Prep5 AI sales analyst, researching a prospect on a laptop"
              className="w-80 lg:w-[480px] h-auto relative z-10 rounded-2xl"
            />

            {/* Floating card: Research ready */}
            <div className="absolute top-6 -right-4 lg:-right-8 z-20 bg-white dark:bg-slate-800 rounded-xl shadow-lg px-3 py-2 border border-gray-100 dark:border-slate-700 max-w-[200px] animate-float-delayed">
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mb-0.5">just now</p>
              <p className="text-xs text-gray-700 dark:text-gray-200 font-medium leading-snug">Sabine found 3 things not on Jane&apos;s LinkedIn, and your next step</p>
            </div>

            {/* Floating card: Research Complete */}
            <div className="absolute bottom-20 -right-6 lg:-right-10 z-20 bg-white dark:bg-slate-800 rounded-xl shadow-lg px-3 py-2 border border-gray-100 dark:border-slate-700 animate-float">
              <div className="flex items-center gap-2">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-200">Research Complete</p>
                <span className="text-xs font-bold text-green-600">100%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .animate-float { animation: float 4s ease-in-out infinite; }
        .animate-float-delayed { animation: float-delayed 4s ease-in-out infinite 1s; }
      `}</style>
    </section>
  );
}
